'use client';

import { useEffect, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowRightIcon,
  CubeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { decodeHtmlEntities } from '@org/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useLanguage } from '@/lib/config/useLanguage';
import { usePathname, useSearchParams } from 'next/navigation';
import { regionalizePath } from '@/lib/config/branches-config';
import { getLeadFormBranch } from '@/lib/forms/region';
import { productItemPath } from '@/lib/catalog/product-path';

const examples = ['Ангары', 'Металлические фермы', 'Ворота'];

export default function SearchLineModal({ open, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  const inputRef = useRef(null);
  const { localizePath } = useLanguage();
  const branch = getLeadFormBranch(
    usePathname() || '/',
    useSearchParams().toString()
  );
  const searchPath = regionalizePath(localizePath('/search/'), branch);
  const normalizedQuery = query.trim();
  const ready = normalizedQuery.length >= 2;
  const current = result?.query === normalizedQuery ? result : null;

  useEffect(() => {
    if (!isOpen || normalizedQuery.length < 2) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setResult(null);
      try {
        const response = await fetch(
          `/api/search/suggestions/?query=${encodeURIComponent(
            normalizedQuery
          )}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (!controller.signal.aborted)
          setResult({ ...data, query: normalizedQuery });
      } catch {
        if (!controller.signal.aborted)
          setResult({ query: normalizedQuery, error: true });
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [isOpen, normalizedQuery, retry]);

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Открыть поиск"
          className="mx-3 hidden h-11 min-w-28 flex-1 items-center gap-2 rounded-xl bg-white/10 px-3 text-sm font-medium text-white transition-colors hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white xl:flex"
        >
          <MagnifyingGlassIcon aria-hidden="true" className="size-5 shrink-0" />
          Поиск
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[85dvh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <div className="px-5 pb-4 pt-6 sm:px-6">
          <DialogTitle className="pr-8 text-xl font-semibold text-slate-900">
            Поиск по каталогу
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm">
            Товары, услуги и решения для вашего проекта.
          </DialogDescription>
          <form action={searchPath} method="get" role="search" className="mt-5">
            <label htmlFor="header-search-query" className="sr-only">
              Поисковый запрос
            </label>
            <div className="flex h-12 items-center gap-3 rounded-xl border border-slate-300 bg-white px-3 focus-within:border-blue-700 focus-within:ring-2 focus-within:ring-blue-700/15">
              <MagnifyingGlassIcon
                aria-hidden="true"
                className="size-5 shrink-0 text-slate-500"
              />
              <input
                ref={inputRef}
                id="header-search-query"
                type="search"
                name="query"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Например, ангар или фермы"
                autoComplete="off"
                minLength={2}
                maxLength={120}
                required
                className="min-w-0 flex-1 bg-transparent text-base text-slate-900 outline-none [&::-webkit-search-cancel-button]:appearance-none"
              />
              {query && (
                <button
                  type="button"
                  aria-label="Очистить запрос"
                  onClick={() => {
                    setQuery('');
                    setResult(null);
                    inputRef.current?.focus();
                  }}
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
                >
                  <XMarkIcon aria-hidden="true" className="size-4" />
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={!ready}
              className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Все результаты
              <ArrowRightIcon aria-hidden="true" className="size-4" />
            </button>
          </form>
        </div>
        <div
          className="min-h-36 overflow-y-auto border-t border-slate-100 px-5 py-4 sm:px-6"
          aria-live="polite"
          aria-busy={ready && !current}
        >
          {!ready ? (
            <>
              <p className="text-sm text-slate-500">
                Введите хотя бы 2 символа или выберите пример:
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {examples.map((example) => (
                  <button
                    type="button"
                    key={example}
                    onClick={() => {
                      setQuery(example);
                      inputRef.current?.focus();
                    }}
                    className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700 hover:bg-slate-200"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </>
          ) : !current ? (
            <div role="status">
              <p className="text-sm text-slate-500">Ищем подходящие решения…</p>
              <div aria-hidden="true" className="mt-3 space-y-3">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-11 animate-pulse rounded-lg bg-slate-100"
                  />
                ))}
              </div>
            </div>
          ) : current.error ? (
            <div>
              <p className="text-sm text-slate-600">
                Не удалось загрузить быстрые результаты. Попробуйте ещё раз или
                откройте полный поиск.
              </p>
              <button
                type="button"
                onClick={() => {
                  setResult(null);
                  setRetry((value) => value + 1);
                }}
                className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200"
              >
                Повторить
              </button>
            </div>
          ) : current.items.length === 0 ? (
            <p className="text-sm text-slate-600">
              По этому запросу ничего не найдено. Попробуйте другое название.
            </p>
          ) : (
            <>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                Быстрые результаты
              </p>
              <ul className="space-y-1">
                {current.items.map((item) => (
                  <li key={item.code}>
                    <a
                      href={productItemPath(item.code, branch, localizePath)}
                      className="flex min-h-14 items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-blue-700"
                    >
                      <CubeIcon
                        aria-hidden="true"
                        className="size-5 shrink-0 text-slate-400"
                      />
                      <span className="min-w-0 flex-1">
                        {decodeHtmlEntities(item.name)}
                      </span>
                      <ArrowRightIcon
                        aria-hidden="true"
                        className="size-4 shrink-0 text-slate-400"
                      />
                    </a>
                  </li>
                ))}
              </ul>
              <a
                href={`${searchPath}?query=${encodeURIComponent(
                  normalizedQuery
                )}`}
                className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-blue-800 hover:underline"
              >
                Все результаты: {current.totalItems}
                <ArrowRightIcon aria-hidden="true" className="size-4" />
              </a>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
